import type { CensusBambooHrResult } from "@/api/census-bamboohr-client";
import type { CensusQuickBooksResult } from "@/api/census-quickbooks-client";
import {
  type StepBuildCeremony,
  mergeStepBuildProps,
  pageBodyBlockBuildProps,
  sectionPageIntroMotions,
} from "@/components/benefits-discovery-v2/step-sequential-build";
import { useCensusBambooHr } from "@/hooks/use-census-bamboohr";
import type { CensusConnectStep } from "@/hooks/use-census-hr-connect";
import { useCensusQuickBooks } from "@/hooks/use-census-quickbooks";
import { cn } from "@/lib/utils";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  PencilLine,
  Plug,
  ShieldCheck,
} from "@cakewalk/ui/icons";
import { V2Button, V2EnrollmentFlowMain, V2Spinner, V2Typography } from "@cakewalk/ui/v2";
import { useState } from "react";
import { BambooHRDomainModal } from "./BambooHRDomainModal";

/**
 * "Bring in your roster" step — shown after the business step, before the team
 * step. The employer either connects a payroll / HR system (preferred: we pull
 * the roster in for them) or enters people by hand.
 *
 * The provider cards reuse the platforms-demo setup-method grid verbatim (the
 * `bd-v2-provider-*` / `cw-v2-setup-provider-card` markup + classes, already
 * present in this app's `styles/cakewalk-v2.css` and scoped under the
 * `.bd-v2-flow` root that `CompanyCensusExperience` sets), so the two supported
 * systems look and animate exactly like the demo's full list.
 *
 * Unlike the demo — which mocks the connection — both cards open a REAL OAuth
 * popup. QuickBooks goes straight there; BambooHR first collects the employer's
 * subdomain in a modal, because BambooHR's authorize and token URLs are both
 * per-tenant and the server can't mint the OAuth URL without it. On a successful
 * pull we hand the mapped roster + business back to the orchestrator, which seeds
 * "Review your team".
 */

type CensusProviderId = "quickbooks-payroll" | "bamboohr";

type CensusPayrollProvider = {
  id: CensusProviderId;
  name: string;
  description: string;
  logoSrc: string;
  /** Featured cell gets the demo's one-shot "beacon" pulse. */
  featured?: boolean;
};

/**
 * Only the two systems the backend can actually pull from. Copy matches
 * `INITIAL_BUNDLE_PAYROLL_PROVIDERS` in platforms-demo so the surfaces read the
 * same.
 */
const CENSUS_PAYROLL_PROVIDERS: CensusPayrollProvider[] = [
  {
    id: "quickbooks-payroll",
    name: "QuickBooks Payroll",
    description: "Your team and pay info — already in QuickBooks.",
    logoSrc: "/payroll-logos/quickbooks.png",
    featured: true,
  },
  {
    id: "bamboohr",
    name: "BambooHR",
    description: "Your HR roster and work emails, brought over for you.",
    logoSrc: "/payroll-logos/bamboohr.png",
  },
];

/** Display name for a roster already pulled in this session. */
const IMPORTED_SOURCE_NAME: Record<"quickbooks" | "bamboohr", string> = {
  quickbooks: "QuickBooks",
  bamboohr: "BambooHR",
};

/** Per-provider busy copy, keyed by the shared connect machine's step. */
const BUSY_LABEL: Record<CensusProviderId, Partial<Record<CensusConnectStep, string>>> = {
  "quickbooks-payroll": {
    authorizing: "Opening QuickBooks…",
    connecting: "Waiting for you to authorize in QuickBooks…",
    fetching: "Importing your team from QuickBooks…",
  },
  bamboohr: {
    authorizing: "Opening BambooHR…",
    connecting: "Waiting for you to authorize in BambooHR…",
    fetching: "Importing your team from BambooHR…",
  },
};

export function CompanyCensusRosterApproachStep({
  playBuild = false,
  ceremony = null,
  onBack,
  onConnectedPayroll,
  onConnectedBambooHr,
  onChooseManual,
  onConnectClicked,
  onQuickBooksStep,
  onBambooHrStep,
  importedRoster = null,
  onContinueWithImported,
}: {
  playBuild?: boolean;
  ceremony?: StepBuildCeremony | null;
  onBack: () => void;
  /** QuickBooks connected + pulled → advance to the pre-filled team review. */
  onConnectedPayroll: (result: CensusQuickBooksResult) => void;
  /** BambooHR connected + pulled → advance to the pre-filled team review. */
  onConnectedBambooHr: (result: CensusBambooHrResult) => void;
  /** Skip payroll → advance to the empty manual/upload team step. */
  onChooseManual: () => void;
  /**
   * A roster already pulled in this session, if any. Present when the employer
   * stepped BACK here from the review step — the one path that used to have no
   * way forward except running the whole import again.
   */
  importedRoster?: { source: "quickbooks" | "bamboohr"; memberCount: number } | null;
  /** Return to the review step with the roster that's already loaded. */
  onContinueWithImported?: () => void;
  /** Fired when the user clicks a provider card (before OAuth). */
  onConnectClicked?: (provider: CensusProviderId) => void;
  /** Emits each QuickBooks OAuth transition so the flow can track analytics. */
  onQuickBooksStep?: (
    step: CensusConnectStep,
    meta?: { error?: string; result?: CensusQuickBooksResult },
  ) => void;
  /** Emits each BambooHR OAuth transition so the flow can track analytics. */
  onBambooHrStep?: (
    step: CensusConnectStep,
    meta?: { error?: string; result?: CensusBambooHrResult },
  ) => void;
}) {
  const qb = useCensusQuickBooks({ onConnected: onConnectedPayroll, onStep: onQuickBooksStep });
  const bamboo = useCensusBambooHr({
    onConnected: onConnectedBambooHr,
    onStep: onBambooHrStep,
  });
  const [domainModalOpen, setDomainModalOpen] = useState(false);

  const { eyebrowMotion, titleMotion, descriptionMotion } = sectionPageIntroMotions(
    playBuild,
    ceremony,
  );

  // Either card being mid-flow locks the whole grid — two concurrent OAuth
  // popups would race to seed the same roster.
  const isBusy = qb.isBusy || bamboo.isBusy;

  // Body blocks build in sequence, so the resume card taking slot 0 has to push
  // everything below it down one — otherwise two blocks share a delay and the
  // staircase collapses.
  const gridBlock = importedRoster ? 1 : 0;
  const manualBlock = gridBlock + 1;
  const securityBlock = gridBlock + 2;
  const backBlock = gridBlock + 3;

  const connectFor = (id: CensusProviderId) => () => {
    onConnectClicked?.(id);
    if (id === "quickbooks-payroll") {
      void qb.startConnect();
      return;
    }
    // BambooHR needs the subdomain before an authorize URL exists.
    setDomainModalOpen(true);
  };

  const stepFor = (id: CensusProviderId): CensusConnectStep =>
    id === "quickbooks-payroll" ? qb.step : bamboo.step;

  // The modal owns the "opening BambooHR" spinner until the popup exists, so the
  // card's own busy label starts once we're past authorize.
  const busyLabelFor = (id: CensusProviderId): string | undefined => BUSY_LABEL[id][stepFor(id)];

  const activeError =
    qb.step === "error"
      ? qb.error
      : bamboo.step === "error" && !domainModalOpen
        ? bamboo.error
        : null;

  return (
    <V2EnrollmentFlowMain width="default">
      <div className="mx-auto w-full max-w-cw-v2-content-narrow pb-8">
        <div className="mb-8 flex flex-col items-center gap-2 text-center">
          <V2Typography
            variant="overline"
            {...mergeStepBuildProps(eyebrowMotion, { className: "text-cw-v2-reliant-red" })}
          >
            Bring in your roster
          </V2Typography>
          <V2Typography variant="display" as="h1" {...mergeStepBuildProps(titleMotion)}>
            Connect payroll — we&rsquo;ll pull your roster in for you.
          </V2Typography>
          <V2Typography
            variant="body-large"
            muted
            {...mergeStepBuildProps(descriptionMotion, { className: "max-w-3xl" })}
          >
            We&rsquo;ll pull in your team&rsquo;s names, birth dates, and home ZIPs, and fill any
            business details you left blank — you review and edit everything before launch. Nothing
            goes live until you&rsquo;re ready.
          </V2Typography>
        </div>

        <div className="flex flex-col gap-6">
          {/* Already imported in this session → a way back to the review step
              that isn't "run the whole import again". Reconnecting from here is
              still offered below, and is now non-destructive (the pull merges
              onto the roster rather than replacing it) — but it should never be
              the ONLY way forward. */}
          {importedRoster ? (
            <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 0, ceremony))}>
              <section className="flex flex-col gap-4 rounded-[12px] border border-cw-v2-green-200 bg-cw-v2-green-50 p-6 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cw-v2-green-100 text-cw-v2-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                  </span>
                  <div className="flex min-w-0 flex-col gap-1">
                    <V2Typography variant="heading-small" as="h3">
                      Your {IMPORTED_SOURCE_NAME[importedRoster.source]} roster is ready
                    </V2Typography>
                    <V2Typography variant="body" muted>
                      {importedRoster.memberCount}{" "}
                      {importedRoster.memberCount === 1 ? "person is" : "people are"} loaded, with
                      any edits you&rsquo;ve already made. Pick up where you left off.
                    </V2Typography>
                  </div>
                </div>
                <V2Button
                  variant="primary"
                  size="md"
                  className="shrink-0 self-start sm:self-center"
                  disabled={isBusy}
                  onClick={onContinueWithImported}
                  iconSuffix={<ArrowRight className="h-4 w-4" />}
                  data-track="button-company-census-resume-imported-roster"
                >
                  Continue to your team
                </V2Button>
              </section>
            </div>
          ) : null}

          {/* Preferred path — the supported systems, as the demo's provider grid. */}
          <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, gridBlock, ceremony))}>
            <div className="bd-v2-provider-section flex flex-col">
              {/* The grid reserves its own top padding for the hover lift, so the
                  heading needs no extra bottom margin on top of it. */}
              <V2Typography variant="heading-medium" as="h2">
                Click your platform and connect
              </V2Typography>

              {/* The demo writes `gap-cw-v2-20` here, but no such spacing token
                  exists — it resolves to nothing. Use a real scale value. */}
              <div className="bd-v2-provider-grid grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2">
                {CENSUS_PAYROLL_PROVIDERS.map((provider) => {
                  const busyLabel = busyLabelFor(provider.id);

                  return (
                    <div
                      key={provider.id}
                      className={cn(
                        "bd-v2-provider-cell",
                        provider.featured && "bd-v2-provider-cell--featured",
                      )}
                    >
                      {/* The whole card is the control (the demo uses a clickable
                          div; a real <button> keeps the same affordance while
                          staying keyboard-reachable). */}
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={connectFor(provider.id)}
                        aria-label={`Connect ${provider.name}`}
                        className={cn(
                          "group cw-v2-setup-provider-card relative flex w-full flex-col gap-3 overflow-hidden rounded-[12px] bg-transparent p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cw-v2-primary focus-visible:ring-offset-2",
                          isBusy ? "cursor-default" : "cursor-pointer",
                        )}
                      >
                        <span className="bd-v2-provider-action" aria-hidden>
                          <Plug className="bd-v2-provider-action-icon" strokeWidth={2.25} />
                        </span>
                        <span className="bd-v2-provider-logo flex shrink-0 items-center">
                          <img
                            src={provider.logoSrc}
                            alt={`${provider.name} logo`}
                            className="bd-v2-provider-logo-img"
                            loading="lazy"
                          />
                        </span>
                        <V2Typography
                          variant="heading-small"
                          as="span"
                          className="bd-v2-provider-title shrink-0 font-semibold"
                        >
                          {provider.name}
                        </V2Typography>
                        <V2Typography
                          variant="body"
                          as="span"
                          muted
                          className="bd-v2-provider-description min-h-0 flex-1"
                        >
                          {provider.description}
                        </V2Typography>

                        {busyLabel ? (
                          <span className="mt-auto flex items-center gap-2 self-start">
                            <V2Spinner size="sm" />
                            <V2Typography variant="meta" muted>
                              {busyLabel}
                            </V2Typography>
                          </span>
                        ) : (
                          /* Visual affordance only — the card itself is the button, so
                             this carries the demo's link chrome without nesting a
                             second interactive element inside it. */
                          <span
                            className="bd-v2-provider-connect cw-v2-button cw-v2-button--link cw-v2-button--sm mt-auto self-start"
                            data-track={`button-company-census-connect-${provider.id}`}
                          >
                            {stepFor(provider.id) === "error"
                              ? "Try connecting again"
                              : `Connect ${provider.name}`}
                            <ArrowRight
                              className="bd-v2-provider-connect-arrow h-4 w-4"
                              aria-hidden
                            />
                          </span>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {activeError ? (
                <div className="mt-4 flex items-start gap-2 rounded-[12px] border border-cw-v2-red-200 bg-cw-v2-red-50 p-3">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-cw-v2-reliant-red" />
                  <V2Typography variant="meta" className="text-cw-v2-reliant-red">
                    {activeError}
                  </V2Typography>
                </div>
              ) : null}
            </div>
          </div>

          {/* Escape hatch — manual entry, de-emphasised. Advances to the existing
              team step exactly as it works today. */}
          <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, manualBlock, ceremony))}>
            <section className="flex flex-col gap-4 rounded-[12px] border border-cw-v2-border p-6 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cw-v2-sidewalk text-cw-v2-text-muted">
                  <PencilLine className="h-4 w-4" />
                </span>
                <div className="flex min-w-0 flex-col gap-1">
                  <V2Typography variant="heading-small" as="h3">
                    Use a different system?
                  </V2Typography>
                  <V2Typography variant="body" muted>
                    No problem — adding your team by hand is quick too. Upload a file or add people
                    one at a time.
                  </V2Typography>
                </div>
              </div>
              <V2Button
                variant="secondary"
                size="md"
                className="shrink-0 self-start sm:self-center"
                disabled={isBusy}
                onClick={onChooseManual}
                data-track="button-company-census-enter-manually"
              >
                Enter people manually
              </V2Button>
            </section>
          </div>

          {/* Mirrors the demo's `BundleSplitSecurityNote` (mono tone). */}
          <div
            {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, securityBlock, ceremony), {
              className: "flex justify-center px-2 text-cw-v2-text-muted",
            })}
          >
            <div className="flex max-w-xl items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {/* `overline` hard-sets uppercase in components.css, which shouts for a
                  sentence like this — use body-small and match the demo's mono size. */}
              <V2Typography variant="body-small" as="span" muted className="text-left">
                Your data is secure and always private. Bank-level encryption.
              </V2Typography>
            </div>
          </div>

          <div
            {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, backBlock, ceremony), {
              className: "flex justify-center",
            })}
          >
            <V2Button
              variant="link"
              onClick={onBack}
              disabled={isBusy}
              data-track="button-company-census-roster-approach-back"
            >
              Back
            </V2Button>
          </div>
        </div>
      </div>

      <BambooHRDomainModal
        open={domainModalOpen}
        onOpenChange={setDomainModalOpen}
        isBusy={bamboo.step === "authorizing"}
        error={bamboo.step === "error" ? bamboo.error : null}
        onSubmitDomain={(companyDomain) => {
          // Close on submit: the OAuth popup takes over from here, and the card's
          // busy label carries the rest of the flow.
          setDomainModalOpen(false);
          void bamboo.startConnect(companyDomain);
        }}
      />
    </V2EnrollmentFlowMain>
  );
}
